import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Divider
} from '@mui/material';

// Configuration automatique de l'API
// Utilise HTTPS en production pour éviter les problèmes de Mixed Content
const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:3001/api'
  : `${window.location.protocol}//${window.location.hostname}/api`;

export default function LiveMatch() {
  const [loading, setLoading] = useState(true);
  const [matchData, setMatchData] = useState(null);
  const [stats, setStats] = useState([]);
  const [substitutions, setSubstitutions] = useState([]);
  const [playerComposition, setPlayerComposition] = useState({});
  const [substitutes, setSubstitutes] = useState([]);
  const [compositionData, setCompositionData] = useState([]);
  const [error, setError] = useState(null);
  const [matchId, setMatchId] = useState(null);

  // Charger les données initiales
  useEffect(() => {
    // Changer le titre de la page
    document.title = 'ESMANAGER - MATCH EN DIRECT';
    
    loadMatchData();
  }, []);

  // Auto-refresh toutes les 30 secondes
  useEffect(() => {
    if (matchId) {
      const interval = setInterval(() => {
        refreshLiveData();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [matchId]);

  // Fonction pour rafraîchir uniquement les données live (score, stats, period)
  const refreshLiveData = async () => {
    if (!matchId) return;
    
    try {
      // Récupérer le match en cours (pour le score et period)
      const ongoingResponse = await fetch(`${API_URL}/matches/ongoing`);
      if (!ongoingResponse.ok) return;
      
      const ongoingMatch = await ongoingResponse.json();
      if (!ongoingMatch) {
        setError('Aucun match en cours');
        setMatchData(null);
        setMatchId(null);
        return;
      }
      
      setMatchData(ongoingMatch);

      // Charger les statistiques
      const statsResponse = await fetch(`${API_URL}/match-stats-simple/${matchId}`);
      const statsData = await statsResponse.json();
      setStats(statsData);

      // Charger les changements
      const subsResponse = await fetch(`${API_URL}/match-subs/${matchId}`);
      const subsData = await subsResponse.json();
      setSubstitutions(subsData);
    } catch (error) {
      console.error('Erreur rafraîchissement données live:', error);
    }
  };

  const loadMatchData = async () => {
    try {
      // Récupérer le match en cours
      const ongoingResponse = await fetch(`${API_URL}/matches/ongoing`);
      if (!ongoingResponse.ok) {
        throw new Error('Erreur lors de la récupération du match en cours');
      }
      const ongoingMatch = await ongoingResponse.json();
      
      // Si pas de match en cours
      if (!ongoingMatch) {
        setError('Aucun match en cours');
        setMatchData(null);
        setMatchId(null);
        setLoading(false);
        return;
      }
      
      const currentMatchId = ongoingMatch.rowid;
      setMatchId(currentMatchId);
      setMatchData(ongoingMatch);

      // Charger les statistiques
      const statsResponse = await fetch(`${API_URL}/match-stats-simple/${currentMatchId}`);
      const statsData = await statsResponse.json();
      setStats(statsData);

      // Charger la composition du match
      const compoResponse = await fetch(`${API_URL}/composition/${ongoingMatch.id_composition}`);
      const compoData = await compoResponse.json();
      setCompositionData(compoData);

      // Charger les joueurs avec leurs positions exactes
      const playersResponse = await fetch(`${API_URL}/match-players/${currentMatchId}`);
      const playersData = await playersResponse.json();
      setPlayerComposition(playersData.composition || {});
      setSubstitutes(playersData.substitutes || []);

      // Charger les changements
      const subsResponse = await fetch(`${API_URL}/match-subs/${currentMatchId}`);
      const subsData = await subsResponse.json();
      setSubstitutions(subsData);

      setError(null);
    } catch (error) {
      console.error('Erreur chargement données match:', error);
      setError('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const getStatsSummary = () => {
    const summary = {};
    stats.forEach(stat => {
      if (!summary[stat.action]) {
        summary[stat.action] = { home: 0, away: 0 };
      }
      if (stat.team === 'home') {
        summary[stat.action].home++;
      } else {
        summary[stat.action].away++;
      }
    });
    
    // Gérer les fautes : comparer "Faut subit" (home) vs "Faut commise" (away)
    if (summary['Faut subit'] || summary['Faut commise']) {
      summary['Fautes'] = {
        home: summary['Faut subit']?.home || 0,
        away: summary['Faut commise']?.home || 0
      };
      // Supprimer les anciennes entrées
      delete summary['Faut subit'];
      delete summary['Faut commise'];
    }
    
    return summary;
  };

  const getGoalsWithDetails = () => {
    const goals = [];
    const buts = stats.filter(stat => stat.action === 'BUT');
    
    buts.forEach(but => {
      const minuteOnly = but.minute.split(':')[0];
      
      if (but.team === 'home') {
        // Pour notre équipe : grouper par joueur
        const scorer = but.id_player ? `${but.firstname} ${but.lastname}` : 'Joueur inconnu';
        const existingGoal = goals.find(g => g.team === 'home' && g.scorer === scorer);
        
        if (existingGoal) {
          existingGoal.minutes.push(minuteOnly);
        } else {
          // Chercher la passe décisive correspondante
          const assist = stats.find(stat => 
            stat.action === 'PASSE DECISIVE' && 
            stat.minute === but.minute && 
            stat.team === but.team
          );
          
          goals.push({
            team: but.team,
            scorer: scorer,
            minutes: [minuteOnly],
            assists: assist && assist.id_player ? [`${assist.firstname} ${assist.lastname}`] : []
          });
        }
      } else {
        // Pour l'équipe adverse : chaque but individuellement
        // Chercher la passe décisive correspondante
        const assist = stats.find(stat => 
          stat.action === 'PASSE DECISIVE' && 
          stat.minute === but.minute && 
          stat.team === but.team
        );
        
        goals.push({
          team: but.team,
          scorer: '', // Pas de nom pour l'équipe adverse
          minutes: [minuteOnly],
          assists: assist && assist.id_player ? [`${assist.firstname} ${assist.lastname}`] : []
        });
      }
    });
    
    // Trier par minute
    return goals.sort((a, b) => {
      const minA = parseInt(a.minutes[0]);
      const minB = parseInt(b.minutes[0]);
      return minA - minB;
    });
  };

  const getChronologicalEvents = () => {
    const events = [];
    
    // Ajouter les buts
    stats.filter(stat => stat.action === 'BUT').forEach(but => {
      const minuteOnly = but.minute.split(':')[0];
      const scorer = but.id_player ? `${but.firstname} ${but.lastname}` : (but.team === 'home' ? 'Joueur inconnu' : '');
      
      // Chercher la passe décisive correspondante
      const assist = stats.find(stat => 
        stat.action === 'PASSE DECISIVE' && 
        stat.minute === but.minute && 
        stat.team === but.team
      );
      
      events.push({
        type: 'goal',
        minute: minuteOnly,
        team: but.team,
        player: scorer,
        assist: assist && assist.id_player ? `${assist.firstname} ${assist.lastname}` : null
      });
    });
    
    // Ajouter les changements
    substitutions.forEach(sub => {
      const minuteOnly = sub.minute.split(':')[0];
      const playerInName = sub.player_in_name || 'Joueur inconnu';
      const playerOutName = sub.player_out_name || 'Joueur inconnu';
      
      events.push({
        type: 'substitution',
        minute: minuteOnly,
        team: 'home', // Les changements sont toujours pour notre équipe
        playerIn: playerInName,
        playerOut: playerOutName
      });
    });
    
    // Ajouter les cartons
    stats.filter(stat => stat.action === 'Carton Jaune' || stat.action === 'Carton Rouge').forEach(card => {
      const minuteOnly = card.minute.split(':')[0];
      const player = card.id_player ? `${card.firstname} ${card.lastname}` : (card.team === 'home' ? 'Joueur inconnu' : '');
      
      events.push({
        type: 'card',
        minute: minuteOnly,
        team: card.team,
        player: player,
        cardType: card.action === 'Carton Jaune' ? 'yellow' : 'red'
      });
    });
    
    // Trier par minute (ordre décroissant - plus récent en haut)
    return events.sort((a, b) => parseInt(b.minute) - parseInt(a.minute));
  };

  // Générer les positions depuis la composition (comme dans Composition.js)
  const generatePositions = () => {
    if (!compositionData || compositionData.length === 0) return [];
    
    const positions = [];
    let positionIndex = 0;
    
    compositionData.forEach((poste) => {
      const { nbPlayer_poste, poste_label } = poste;
      
      for (let i = 0; i < nbPlayer_poste; i++) {
        let x = 50;
        let y = 50;
        
        if (poste_label === 'Attaquant') {
          if (nbPlayer_poste === 1) {
            x = 50;
            y = 10;
          } else if (nbPlayer_poste === 2) {
            x = i === 0 ? 30 : 70;
            y = 10;
          } else if (nbPlayer_poste === 3) {
            if (i === 1) {
              x = 50;
              y = 5;
            } else {
              x = i === 0 ? 20 : 80;
              y = 15;
            }
          } else if (nbPlayer_poste === 4) {
            if (i < 2) {
              x = i === 0 ? 15 : 85;
              y = 5;
            } else {
              x = i === 2 ? 30 : 70;
              y = 15;
            }
          }
        } else if (poste_label === 'Milieu') {
          if (nbPlayer_poste === 1) {
            x = 50;
            y = 35;
          } else if (nbPlayer_poste === 2) {
            x = i === 0 ? 30 : 70;
            y = 35;
          } else if (nbPlayer_poste === 3) {
            if (i === 1) {
              x = 50;
              y = 40;
            } else {
              x = i === 0 ? 20 : 80;
              y = 30;
            }
          } else if (nbPlayer_poste === 4) {
            if (i < 2) {
              x = i === 0 ? 15 : 85;
              y = 30;
            } else {
              x = i === 2 ? 30 : 70;
              y = 40;
            }
          } else if (nbPlayer_poste === 5) {
            if (i < 3) {
              x = 15 + (i * 35);
              y = 30;
            } else {
              x = i === 3 ? 30 : 70;
              y = 45;
            }
          }
        } else if (poste_label === 'Défenseur') {
          if (nbPlayer_poste === 1) {
            x = 50;
            y = 65;
          } else if (nbPlayer_poste === 2) {
            x = i === 0 ? 30 : 70;
            y = 65;
          } else if (nbPlayer_poste === 3) {
            if (i === 1) {
              x = 50;
              y = 70;
            } else {
              x = i === 0 ? 20 : 80;
              y = 60;
            }
          } else if (nbPlayer_poste === 4) {
            if (i < 2) {
              x = i === 0 ? 15 : 85;
              y = 60;
            } else {
              x = i === 2 ? 30 : 70;
              y = 70;
            }
          } else if (nbPlayer_poste === 5) {
            if (i < 2) {
              x = i === 0 ? 15 : 85;
              y = 60;
            } else {
              x = 20 + ((i - 2) * 30);
              y = 70;
            }
          }
        } else if (poste_label === 'Gardien') {
          x = 50;
          y = 85;
        }
        
        positions.push({
          id: `${poste_label.toLowerCase()}_${i + 1}`,
          label: poste_label.toUpperCase().substring(0, 3),
          x: x,
          y: y,
          poste: poste_label
        });
        
        positionIndex++;
      }
    });
    
    return positions;
  };

  const fieldPositions = generatePositions();
  
  // Récupérer le joueur pour une position donnée depuis playerComposition
  const getPlayerForPosition = (position) => {
    if (!playerComposition || Object.keys(playerComposition).length === 0) return null;
    
    // Chercher le joueur avec la position_key exacte
    const player = playerComposition[position.id];
    
    return player || null;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#f5f5f5' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !matchData) {
    return (
    <Box sx={{ bgcolor: '#f5f5f5' }}>
      {/* Header avec logo */}
      <Box sx={{ bgcolor: 'white', p: 2, textAlign: 'center', boxShadow: 1 }}>
        <img src="/assets/logo_esma.png" alt="logo" style={{ height: '50px' }} />
      </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 3 }}>
          <Box 
            sx={{ 
              bgcolor: 'white', 
              borderRadius: 3, 
              p: 4, 
              textAlign: 'center',
              boxShadow: 2,
              maxWidth: '400px'
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: '#ff9800' }}>
              Aucun match en direct
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Il n'y a actuellement aucun match en cours.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cette page se mettra automatiquement à jour dès qu'un match commencera.
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  const statsSummary = getStatsSummary();

  return (
    <Box sx={{ bgcolor: '#f5f5f5', pb: 4 }}>
      {/* Header avec logo */}
      <Box sx={{ bgcolor: 'white', p: 2, textAlign: 'center', boxShadow: 1 }}>
        <img src="/assets/logo_esma.png" alt="logo" style={{ height: '50px' }} />
      </Box>

      {/* Score principal */}
      <Box sx={{ p: 2, textAlign: 'center', bgcolor: '#fff3e0', mt: 2, mx: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            ESMA
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 'bold', mx: 1 }}>
            {matchData.score_home} - {matchData.score_away}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            {matchData.label_adversaire}
          </Typography>
        </Box>
        <Box sx={{ mt: 1 }}>
          <Chip 
            label={matchData.period === 1 ? '1ère MI-TEMPS' : '2ème MI-TEMPS'} 
            color="warning" 
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          {matchData.match_type} • {matchData.lieu}
        </Typography>
        
        {/* Affichage des buts */}
        {getGoalsWithDetails().length > 0 && (
          <Box sx={{ mt: 2 }}>
            {(() => {
              const homeGoals = getGoalsWithDetails().filter(goal => goal.team === 'home');
              const awayGoals = getGoalsWithDetails().filter(goal => goal.team === 'away');
              const maxGoals = Math.max(homeGoals.length, awayGoals.length);
              
              return Array.from({ length: maxGoals }, (_, index) => (
                <Box key={index} sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 0.5 
                }}>
                  {/* But de notre équipe (gauche) */}
                  <Box sx={{ flex: 1, textAlign: 'left' }}>
                    {homeGoals[index] && (
                      <Typography variant="body2" sx={{ fontWeight: 'normal' }}>
                        {homeGoals[index].minutes.join(', ')}' {homeGoals[index].scorer && <span style={{ fontStyle: 'italic' }}>{homeGoals[index].scorer}</span>}
                      </Typography>
                    )}
                  </Box>
                  
                  {/* But de l'équipe adverse (droite) */}
                  <Box sx={{ flex: 1, textAlign: 'right' }}>
                    {awayGoals[index] && (
                      <Typography variant="body2" sx={{ fontWeight: 'normal' }}>
                        {awayGoals[index].minutes.join(', ')}'
                      </Typography>
                    )}
                  </Box>
                </Box>
              ));
            })()}
          </Box>
        )}
        
      </Box>

      {/* Statistiques comparatives */}
      <Box sx={{ mt: 3, mx: 2 }}>
        <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center' }}>
            Statistiques
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableBody>
                {Object.entries(statsSummary)
                  .filter(([action]) => !['PASSE DECISIVE', 'Changement'].includes(action))
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([action, counts]) => {
                    const total = counts.home + counts.away;
                    const homePercent = total > 0 ? (counts.home / total) * 100 : 0;
                    const awayPercent = total > 0 ? (counts.away / total) * 100 : 0;

                    return (
                      <TableRow key={action}>
                        <TableCell align="right" sx={{ width: '20%', fontWeight: 'bold' }}>
                          {counts.home}
                        </TableCell>
                        <TableCell sx={{ width: '60%' }}>
                          <Box sx={{ position: 'relative', height: 30, display: 'flex', alignItems: 'center' }}>
                            <Box
                              sx={{
                                position: 'absolute',
                                left: 0,
                                width: `${homePercent}%`,
                                height: '100%',
                                bgcolor: '#1976d2',
                                borderRadius: '4px 0 0 4px'
                              }}
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                right: 0,
                                width: `${awayPercent}%`,
                                height: '100%',
                                bgcolor: '#d32f2f',
                                borderRadius: '0 4px 4px 0'
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                position: 'relative',
                                zIndex: 1,
                                width: '100%',
                                textAlign: 'center',
                                fontWeight: 'bold',
                                color: total > 0 ? 'white' : 'text.primary'
                              }}
                            >
                              {action}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="left" sx={{ width: '20%', fontWeight: 'bold' }}>
                          {counts.away}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      {/* Composition */}
      <Box sx={{ mt: 3, mx: 2 }}>
        <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center' }}>
            Composition
          </Typography>

          {/* Terrain */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '400px',
              backgroundColor: '#2e7d32',
              borderRadius: 3,
              border: '3px solid #fff',
              mb: 4,
              overflow: 'hidden'
            }}
          >
            {/* Lignes du terrain */}
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: 0,
              right: 0,
              height: '2px',
              backgroundColor: '#fff',
              transform: 'translateY(-50%)'
            }} />
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: '20px',
              height: '20px',
              backgroundColor: '#fff',
              borderRadius: '50%',
              transform: 'translate(-50%, -50%)'
            }} />

            {/* Joueurs */}
            {fieldPositions.map((position) => {
              const player = getPlayerForPosition(position);
              return (
                <Box
                  key={position.id}
                  sx={{
                    position: 'absolute',
                    left: `${position.x}%`,
                    top: `${position.y}%`,
                    transform: 'translate(-50%, -50%)',
                    minWidth: '80px',
                    textAlign: 'center'
                  }}
                >
                  {player ? (
                    <Box>
                      <Typography
                        sx={{
                          color: '#fff',
                          backgroundColor: 'rgba(0,0,0,0.5)',
                          borderRadius: 1,
                          padding: '4px 8px',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {player.firstname}. {player.lastname?.charAt(0)}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography
                      sx={{
                        color: '#fff',
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        borderRadius: 1,
                        padding: '4px 8px',
                        fontSize: '0.75rem',
                        fontWeight: 'bold'
                      }}
                    >
                      {position.label}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Remplaçants */}
          {substitutes.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Remplaçants
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {substitutes.map((sub) => (
                  <Box
                    key={sub.id_player}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      bgcolor: '#f5f5f5',
                      borderRadius: 1
                    }}
                  >
                    <Box
                      sx={{
                        width: '30px',
                        height: '30px',
                        borderRadius: '50%',
                        bgcolor: '#757575',
                        border: '2px solid white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.7rem'
                      }}
                    >
                      {sub.firstname[0]}{sub.lastname[0]}
                    </Box>
                    <Typography variant="body2">
                      {sub.firstname} {sub.lastname}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>


      {/* Chronologie du match */}
      {getChronologicalEvents().length > 0 && (
        <Box sx={{ mt: 3, mx: 2 }}>
          <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center' }}>
              Chronologie du match
            </Typography>
            
            <Box sx={{ width: '100%' }}>
              {getChronologicalEvents().map((event, index) => (
                <Box key={index} sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 1,
                  justifyContent: event.team === 'home' ? 'flex-start' : 'flex-end'
                }}>
                  {event.team === 'home' ? (
                    // Événements de notre équipe (gauche)
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Typography variant="caption" sx={{ minWidth: '40px', fontWeight: 'bold' }}>
                        {event.minute}'
                      </Typography>
                      <Box sx={{ ml: 1 }}>
                        {event.type === 'goal' && (
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            BUT - {event.player}
                            {event.assist && (
                              <span style={{ fontStyle: 'italic', fontWeight: 'normal' }}> ({event.assist})</span>
                            )}
                          </Typography>
                        )}
                        {event.type === 'substitution' && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box component="span" sx={{ color: '#d32f2f', fontWeight: 'bold', fontSize: '0.875rem' }}>
                              ↓ {event.playerOut}
                            </Box>
                            <Box component="span" sx={{ color: '#4caf50', fontWeight: 'bold', fontSize: '0.875rem' }}>
                              ↑ {event.playerIn}
                            </Box>
                          </Box>
                        )}
                        {event.type === 'card' && (
                          <Typography variant="body2">
                            <span style={{ color: event.cardType === 'red' ? '#f44336' : '#ff9800' }}>
                              Carton {event.cardType === 'red' ? 'Rouge' : 'Jaune'}
                            </span> - {event.player}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ) : (
                    // Événements de l'équipe adverse (droite)
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'flex-end' }}>
                      <Box sx={{ textAlign: 'right' }}>
                        {event.type === 'goal' && (
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {event.player ? `${event.player} - BUT` : 'BUT'}
                            {event.assist && (
                              <span style={{ fontStyle: 'italic', fontWeight: 'normal' }}> ({event.assist})</span>
                            )}
                          </Typography>
                        )}
                        {event.type === 'substitution' && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: 'row-reverse' }}>
                            <Box component="span" sx={{ color: '#d32f2f', fontWeight: 'bold', fontSize: '0.875rem' }}>
                              ↓ {event.playerOut}
                            </Box>
                            <Box component="span" sx={{ color: '#4caf50', fontWeight: 'bold', fontSize: '0.875rem' }}>
                              ↑ {event.playerIn}
                            </Box>
                          </Box>
                        )}
                        {event.type === 'card' && (
                          <Typography variant="body2">
                            {event.player ? `${event.player} - ` : ''}<span style={{ color: event.cardType === 'red' ? '#f44336' : '#ff9800' }}>
                              Carton {event.cardType === 'red' ? 'Rouge' : 'Jaune'}
                            </span>
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ minWidth: '40px', fontWeight: 'bold', ml: 1 }}>
                        {event.minute}'
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {/* Footer */}
      <Box sx={{ mt: 4, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="caption">
          Actualisation automatique toutes les 30 secondes
        </Typography>
      </Box>
    </Box>
  );
}

